import {
  MIN_MARKETS_PER_CATEGORY,
  MARKET_CATEGORY_FALLBACKS,
  MARKET_CATEGORIES_TO_DISPLAY
} from '@/app/data/market'
import { MarketCategoryType } from '@/types'
import type { PriceLevel } from '@injectivelabs/sdk-ts'
import type { SharedUiMarketHistory } from '@shared/types'
import type {
  ChartPoint,
  UiMarketWithToken,
  MarketCategoryCountMap,
  FoldedMarketCategoryMap
} from '@/types'

interface PriceLevelMap {
  [price: string]: PriceLevel
}

export const getFormattedMarketsHistoryChartData = (
  marketsHistory: SharedUiMarketHistory
): ChartPoint[] => {
  return marketsHistory.time.map((time, index) => {
    const openPrice = marketsHistory.openPrice[index] || 0
    const closePrice = marketsHistory.closePrice[index] || 0

    return [time, openPrice, closePrice]
  })
}

/**
 * 1. if new exists in current, update quantity in current,
 * 2. if new exists in current and quantity is 0, delete from current
 * 3. If new doesn't exist in current, add to current
 **/
const updateOrderbookRecord = (
  currentRecords: PriceLevel[] = [],
  updatedRecords: PriceLevel[] = []
) => {
  const currentRecordsMap: PriceLevelMap = currentRecords.reduce(
    (currentRecordsMap, record) => {
      currentRecordsMap[record.price] = record

      return currentRecordsMap
    },
    {} as PriceLevelMap
  )

  updatedRecords.forEach((record) => {
    currentRecordsMap[record.price] = record
  })

  return Object.values(currentRecordsMap).filter((record) =>
    toBigNumber(record.quantity).gt(0)
  )
}

export const combineOrderbookRecords = ({
  isBuy,
  updatedRecords = [],
  currentRecords = []
}: {
  isBuy: boolean
  updatedRecords?: PriceLevel[]
  currentRecords?: PriceLevel[]
}) => {
  const combinedOrderbookRecords = updateOrderbookRecord(
    currentRecords,
    updatedRecords
  )

  return combinedOrderbookRecords.sort((a, b) => {
    return isBuy
      ? toBigNumber(b.price).minus(a.price).toNumber()
      : toBigNumber(a.price).minus(b.price).toNumber()
  })
}

export function getFoldedMarketCategoryMap(
  markets: UiMarketWithToken[],
  marketCategoriesToKeepVisible: MarketCategoryType[]
) {
  const marketCategoryCountMap = getMarketCountPerCategory(markets)

  return MARKET_CATEGORIES_TO_DISPLAY.reduce((foldedMap, category) => {
    if (!isMarketCategoryFoldable(category, marketCategoriesToKeepVisible)) {
      return foldedMap
    }

    const marketCount = marketCategoryCountMap[category] || 0
    if (marketCount === 0 || marketCount >= MIN_MARKETS_PER_CATEGORY) {
      return foldedMap
    }

    const fallbackCategory = MARKET_CATEGORY_FALLBACKS[category]

    if (fallbackCategory) {
      foldedMap[category] = fallbackCategory
    }

    return foldedMap
  }, {} as FoldedMarketCategoryMap)
}

export function getAvailableMarketCategoryFilters(
  markets: UiMarketWithToken[],
  foldedMarketCategoryMap: FoldedMarketCategoryMap,
  marketCategoriesToKeepVisible: MarketCategoryType[]
) {
  const marketCategoryCountMap = getMarketCountPerCategory(markets)
  const fallbackMarketCategories = new Set(
    Object.values(foldedMarketCategoryMap)
  )

  return MARKET_CATEGORIES_TO_DISPLAY.filter((category) => {
    if (!isMarketCategoryFoldable(category, marketCategoriesToKeepVisible)) {
      return true
    }

    return (
      (!foldedMarketCategoryMap[category] &&
        Boolean(marketCategoryCountMap[category])) ||
      fallbackMarketCategories.has(category)
    )
  })
}

export function resolveMarketCategoryFilter(
  category: MarketCategoryType,
  markets: UiMarketWithToken[],
  foldedMarketCategoryMap: FoldedMarketCategoryMap,
  marketCategoriesToKeepVisible: MarketCategoryType[]
) {
  const availableMarketCategories = getAvailableMarketCategoryFilters(
    markets,
    foldedMarketCategoryMap,
    marketCategoriesToKeepVisible
  )

  if (availableMarketCategories.includes(category)) {
    return category
  }

  return foldedMarketCategoryMap[category] || MarketCategoryType.All
}

export function getCategoryFiltersForMarket(
  market: UiMarketWithToken,
  foldedMarketCategoryMap: FoldedMarketCategoryMap
) {
  return market.categories.map(
    (category) => foldedMarketCategoryMap[category] || category
  )
}

function isMarketCategoryFoldable(
  category: MarketCategoryType,
  marketCategoriesToKeepVisible: MarketCategoryType[]
) {
  return !marketCategoriesToKeepVisible.includes(category)
}

function getMarketCountPerCategory(markets: UiMarketWithToken[]) {
  return markets.reduce((countMap, market) => {
    market.categories.forEach((category) => {
      countMap[category] = (countMap[category] || 0) + 1
    })

    return countMap
  }, {} as MarketCategoryCountMap)
}
